import { useState } from "react";
import { getFontFamily } from "../constants/colors";

/*
 * PaywallPopup — shows when a free/lite user hits a feature limit
 * 
 * Props:
 *   type: "aiChat" | "practice" | "tests" | "flashCards"
 *   tier: "free" | "lite"  (current tier)
 *   onClose: () => void
 *   onUpgrade: () => void  (navigate to ProPage)
 *   lang: "en" | "hi"
 *   used: number (optional, for limit-hit scenarios)
 *   limit: number (optional)
 */

const MESSAGES = {
  aiChat: {
    free: {
      en: { title: "AI Tutor is a Pro feature", sub: "Get help from your personal AI tutor", cta: "Unlock AI Tutor" },
      hi: { title: "AI Tutor Pro feature है", sub: "अपने AI tutor से मदद पाएं", cta: "AI Tutor अनलॉक करें" },
    },
    lite: {
      en: { title: "Monthly limit reached", sub: "You've used all 20 AI sessions this month", cta: "Upgrade to Pro" },
      hi: { title: "मासिक सीमा पूरी हुई", sub: "इस महीने 20 AI sessions हो गए", cta: "Pro में अपग्रेड करें" },
    },
  },
  practice: {
    free: {
      en: { title: "Practice Solving is a paid feature", sub: "Solve problems step-by-step with AI hints", cta: "Unlock Practice" },
      hi: { title: "Practice Solving paid feature है", sub: "AI hints से step-by-step solve करें", cta: "Practice अनलॉक करें" },
    },
    lite: {
      en: { title: "Monthly limit reached", sub: "You've used all 20 practice sessions", cta: "Upgrade to Pro" },
      hi: { title: "मासिक सीमा पूरी हुई", sub: "इस महीने 20 practice sessions हो गए", cta: "Pro में अपग्रेड करें" },
    },
  },
  tests: {
    free: {
      en: { title: "Tests are a paid feature", sub: "Board-style chapter tests & mock exams", cta: "Unlock Tests" },
      hi: { title: "Tests paid feature है", sub: "बोर्ड स्टाइल chapter tests और mock exams", cta: "Tests अनलॉक करें" },
    },
  },
  flashCards: {
    free: {
      en: { title: "Full Flash Cards is a paid feature", sub: "3 chapters free · Upgrade for all chapters", cta: "Unlock All Cards" },
      hi: { title: "Full Flash Cards paid feature है", sub: "3 chapters free · सभी chapters के लिए upgrade करें", cta: "सभी Cards अनलॉक करें" },
    },
  },
};

// Feature icons
const ICONS = {
  aiChat: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  practice: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round">
      <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  ),
  tests: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
    </svg>
  ),
  flashCards: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  ),
};

export function PaywallPopup({ type = "aiChat", tier = "free", onClose, onUpgrade, lang = "en" }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const msgs = MESSAGES[type]?.[tier] || MESSAGES[type]?.free || MESSAGES.aiChat.free;
  const msg = isHi ? msgs.hi : msgs.en;
  const icon = ICONS[type] || ICONS.aiChat;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(10,24,32,0.6)",
      backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      fontFamily: ff,
      animation: "fadeIn 0.2s ease",
    }}
      onClick={onClose}
    >
      <style>{`
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
        @keyframes slideUp { from { transform:translateY(100%); } to { transform:translateY(0); } }
      `}</style>

      <div style={{
        width: "100%", maxWidth: 420,
        background: "white",
        borderRadius: "24px 24px 0 0",
        padding: "28px 24px 32px",
        animation: "slideUp 0.3s cubic-bezier(0.2, 0.8, 0.3, 1)",
        // WebView-compensated shadow
        boxShadow: "0 -4px 20px rgba(0,0,0,0.2)",
      }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div style={{ width: 40, height: 4, borderRadius: 2, background: "#DAE4EC", margin: "0 auto 20px" }} />

        {/* Icon */}
        <div style={{
          width: 56, height: 56, borderRadius: 16,
          background: "linear-gradient(135deg, #1A7A52, #2AAA72)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 16px",
          boxShadow: "0 4px 14px rgba(26,122,82,0.3)",
        }}>
          {icon}
        </div>

        {/* Title */}
        <div style={{
          fontSize: 20, fontWeight: 700, color: "#0A1820",
          textAlign: "center", letterSpacing: "-0.03em", lineHeight: 1.3,
        }}>
          {msg.title}
        </div>

        {/* Subtitle */}
        <div style={{
          fontSize: 13, color: "#4A6474", textAlign: "center",
          marginTop: 8, lineHeight: 1.5,
        }}>
          {msg.sub}
        </div>

        {/* What you get */}
        <div style={{
          marginTop: 20, background: "#F5F9FB", borderRadius: 14,
          padding: "14px 16px", border: "1px solid #E6EEF5",
        }}>
          {[
            tier === "free"
              ? (isHi ? "AI tutor + Practice solving" : "AI tutor + Practice solving")
              : (isHi ? "Unlimited AI sessions" : "Unlimited AI sessions"),
            isHi ? "Unlimited tests & flash cards" : "Unlimited tests & flash cards",
            tier === "free"
              ? (isHi ? "₹109/mo से शुरू" : "Starting ₹109/mo")
              : (isHi ? "₹199/mo · सब unlimited" : "₹199/mo · Everything unlimited"),
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "6px 0",
              borderBottom: i < 2 ? "1px solid #E8EFF5" : "none",
            }}>
              <div style={{
                width: 20, height: 20, borderRadius: "50%", background: "#E8F5EE",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#1A7A52" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <span style={{ fontSize: 12, color: "#0A1820", fontWeight: 500 }}>{item}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button
          onClick={onUpgrade}
          style={{
            width: "100%", padding: "15px", marginTop: 20,
            background: "#1A7A52", color: "white", border: "none",
            borderRadius: 16, fontSize: 16, fontWeight: 800,
            cursor: "pointer", fontFamily: ff,
            // WebView-compensated shadow
            boxShadow: "0 4px 16px rgba(26,122,82,0.35)",
            letterSpacing: "-0.02em",
          }}
        >
          {msg.cta}
        </button>

        {/* Not now */}
        <div
          onClick={onClose}
          style={{
            textAlign: "center", marginTop: 14,
            fontSize: 13, color: "#6A808C", cursor: "pointer",
            fontWeight: 500,
          }}
        >
          {isHi ? "अभी नहीं" : "Not now"}
        </div>
      </div>
    </div>
  );
}
